import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Play, RotateCcw, Gift, Activity, Trophy } from 'lucide-react';
import axios from 'axios';
import { useWebSocket } from '../hooks/useWebSocket';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RaffleOperator = () => {
  const [raffleState, setRaffleState] = useState({
    state: 'idle',
    winner: null,
    winners_history: []
  });
  const [loading, setLoading] = useState(false);
  const { lastMessage, isConnected } = useWebSocket();

  useEffect(() => {
    fetchState();
  }, []);

  useEffect(() => {
    if (!lastMessage) return;
    
    if (lastMessage.type === 'raffle_start') {
      setRaffleState(prev => ({ ...prev, state: 'running', winner: null }));
    } else if (lastMessage.type === 'raffle_winner') {
      setRaffleState(prev => ({
        ...prev,
        state: 'winner',
        winner: lastMessage.name,
        winners_history: [...prev.winners_history, lastMessage.name]
      }));
    } else if (lastMessage.type === 'raffle_reset') {
      setRaffleState({ state: 'idle', winner: null, winners_history: [] });
    } else if (lastMessage.type === 'init' && lastMessage.raffle) {
      setRaffleState(prev => ({ ...prev, ...lastMessage.raffle }));
    }
  }, [lastMessage]);

  const fetchState = async () => {
    try {
      const res = await axios.get(`${API}/raffle/state`);
      setRaffleState(res.data);
    } catch (e) {
      console.error('Failed to fetch raffle state:', e);
    }
  };

  const handleAction = async (action) => {
    setLoading(true);
    try {
      await axios.post(`${API}/raffle/${action}`);
      await fetchState();
    } catch (e) {
      console.error(`Action ${action} failed:`, e);
    }
    setLoading(false);
  };

  return (
    <div className="operator-layout" data-testid="raffle-operator">
      <div className="wave-background opacity-30" />
      
      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="operator-header">
          <div className="flex items-center gap-4">
            <Link to="/" className="nav-link" data-testid="back-link">
              <ArrowLeft size={20} />
              Back
            </Link>
            <h1 className="text-2xl font-bold text-white font-['Outfit']">
              Raffle Operator
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <div className={`status-badge ${isConnected ? 'active' : 'eliminated'}`}>
              <Activity size={14} />
              {isConnected ? 'Connected' : 'Disconnected'}
            </div>
            <Link 
              to="/raffle/screen" 
              target="_blank"
              className="operator-btn secondary"
              data-testid="view-screen-link"
            >
              View Screen
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="stat-card" data-testid="stat-state">
            <div className="stat-value text-2xl uppercase">{raffleState.state}</div>
            <div className="stat-label">Current State</div>
          </div>
          <div className="stat-card" data-testid="stat-available">
            <div className="stat-value">{50 - (raffleState.winners_history?.length || 0)}</div>
            <div className="stat-label">Available</div>
          </div>
          <div className="stat-card" data-testid="stat-winners">
            <div className="stat-value text-yellow-400">{raffleState.winners_history?.length || 0}</div>
            <div className="stat-label">Winners</div>
          </div>
        </div>

        {/* Current Winner */}
        {raffleState.winner && (
          <div className="operator-card p-8 mb-8 text-center">
            <div className="text-sm text-slate-400 mb-2">Current Winner</div>
            <div className="text-4xl font-bold text-white font-['Outfit'] text-glow">
              {raffleState.winner}
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="operator-card p-6 mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">Controls</h2>
          
          <div className="operator-grid">
            <button
              onClick={() => handleAction('start')}
              className="operator-btn"
              disabled={loading || raffleState.state === 'running'}
              data-testid="start-raffle-btn"
            >
              <Play size={18} className="inline mr-2" />
              Start Raffle
            </button>

            <button
              onClick={() => handleAction('reset')}
              className="operator-btn danger"
              disabled={loading || raffleState.state === 'running'}
              data-testid="reset-raffle-btn"
            >
              <RotateCcw size={18} className="inline mr-2" />
              Reset All
            </button>

            <button
              onClick={() => handleAction('force-winner')}
              className="operator-btn secondary"
              disabled={loading || raffleState.state === 'running'}
              data-testid="force-winner-btn"
            >
              <Gift size={18} className="inline mr-2" />
              Force Winner
            </button>
          </div>
        </div>

        {/* Winners History */}
        {raffleState.winners_history && raffleState.winners_history.length > 0 && (
          <div className="operator-card p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Trophy size={20} className="text-yellow-400" />
              Winners History
            </h2>
            <div className="space-y-2">
              {raffleState.winners_history.map((name, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg"
                >
                  <span className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-400 font-bold text-sm">
                    {index + 1}
                  </span>
                  <span className="text-white">{name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RaffleOperator;
